import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

function safeNextPath(raw) {
  if (!raw || typeof raw !== "string") return "/home";
  // Reject anything that isn't a same-origin path:
  //  - must start with '/'
  //  - must NOT start with '//' (protocol-relative)
  //  - must NOT contain ':' before any '/' (e.g. 'javascript:...')
  //  - must NOT start with '/\\' (Windows-style absolute)
  if (!raw.startsWith("/")) return "/home";
  if (raw.startsWith("//")) return "/home";
  if (raw.startsWith("/\\")) return "/home";
  // Reject any embedded scheme like '/http://evil' just in case
  if (/^\/+(https?:|javascript:|data:)/i.test(raw)) return "/home";
  return raw;
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNextPath(searchParams.get("next"));
  const { login, status } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [demoVisible, setDemoVisible] = useState(false);
  const [demoAccounts, setDemoAccounts] = useState([]);

  useEffect(() => {
    if (status === "authenticated") navigate(nextPath, { replace: true });
  }, [status, navigate, nextPath]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/config");
        if (data.demo_accounts_visible && Array.isArray(data.demo_accounts)) {
          setDemoVisible(true);
          setDemoAccounts(data.demo_accounts);
        }
      } catch (_e) {
        /* non-blocking */
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const fillDemo = (acc) => {
    setEmail(acc.email);
    setPassword(acc.password);
    setError(null);
  };

  return (
    <div
      className="min-h-screen bg-[var(--sa-subtle)] flex flex-col"
      data-testid="login-screen"
    >
      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-[420px]"
        >
          <div className="sa-card px-7 py-9 sm:px-9">
            <div className="flex flex-col items-center">
              <Logo size={150} />
              <p className="mt-3 text-sm font-medium text-slate-500">
                Connexion à votre espace
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="mt-7 flex flex-col gap-4"
              data-testid="login-form"
            >
              <div>
                <label className="sa-label" htmlFor="login-email">
                  Email
                </label>
                <input
                  id="login-email"
                  data-testid="login-email-input"
                  type="email"
                  required
                  autoComplete="email"
                  className="sa-input"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label className="sa-label" htmlFor="login-password">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    data-testid="login-password-input"
                    type={showPwd ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    className="sa-input pr-12"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    data-testid="login-toggle-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600"
                    aria-label={showPwd ? "Cacher le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {error ? (
                <div
                  data-testid="login-error"
                  className="rounded-[12px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
                >
                  {error}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={submitting || !email || !password}
                className="sa-btn-primary mt-1"
                data-testid="login-submit-button"
              >
                {submitting ? "Connexion…" : "Se connecter"}
              </button>
            </form>

            {demoVisible && demoAccounts.length > 0 ? (
              <div
                className="mt-7 rounded-[14px] border border-slate-200 bg-[var(--sa-subtle)] p-4"
                data-testid="demo-accounts-block"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Comptes de démonstration
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Clique pour pré-remplir le formulaire
                </p>
                <div className="mt-3 flex flex-col gap-2">
                  {demoAccounts.map((acc) => (
                    <button
                      key={acc.email}
                      type="button"
                      onClick={() => fillDemo(acc)}
                      data-testid={`demo-account-${acc.email}`}
                      className="flex items-center justify-between rounded-[10px] border border-slate-200 bg-white px-3 py-2.5 text-left transition hover:border-[var(--sa-blue)] hover:shadow-sm"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-slate-900">
                          {acc.full_name}
                        </span>
                        <span className="text-xs text-slate-500">{acc.email}</span>
                      </div>
                      <span className="rounded-full bg-[var(--sa-blue-soft)] px-2.5 py-0.5 text-[11px] font-medium text-[var(--sa-blue-dark)]">
                        {acc.role}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <p className="mt-6 text-center text-xs font-medium text-slate-400">
            Propulsé par C l'Agence
          </p>
        </motion.div>
      </div>
    </div>
  );
}
